import { getFormBuilderItemKindKey, TFormBuilderItemKind } from "../../../model/item/formBuilderItem";
import { useTranslation } from "react-i18next";
import { FormBuilderDraggableItem } from "../../../draggableItem/FormBuilderDraggableItem";
import { useFormBuilderItemMetadataMap } from "../../../hooks/itemMetadataMap/useFormBuilderItemMetadataMap";
import { FormBuilderPanelView } from "../FormBuilderPanelView";
import React, { useMemo, useState } from "react";
import { FormBuilderPanelDivider } from "../../FormBuilderPanelDivider";
import classNames from "classnames";
import { FormBuilderFormSettings } from "../../settings/form/FormBuilderFormSettings";
import "./formBuilderPanelFormView.scss";

interface IFormBuilderDraggableItemCategory {
  heading: string;
  kinds: TFormBuilderItemKind[];
}

type TFormBuilderPanelTab = "items" | "settings";

interface IFormBuilderPanelTabOption {
  tab: TFormBuilderPanelTab;
  label: string;
}

export function FormBuilderPanelFormView() {
  const { t } = useTranslation();

  const metadataMap = useFormBuilderItemMetadataMap();

  const [activeTab, setActiveTab] = useState<TFormBuilderPanelTab>("items");

  const draggableItemCategories: IFormBuilderDraggableItemCategory[] = useMemo(() => [
    {
      heading: t("Contact"),
      kinds: [
        { type: "input", role: "email" },
        { type: "input", role: "first-name" },
        { type: "input", role: "last-name" },
        { type: "input", role: "address" },
        { type: "input", role: "phone" },
        { type: "select", role: "inquiry" },
        { type: "textarea", role: "message" },
      ],
    },
    {
      heading: t("General"),
      kinds: [
        { type: "text", role: "heading" },
        { type: "text", role: "paragraph" },
        { type: "input", role: "custom" },
        { type: "date", role: "custom" },
        { type: "checkbox", role: "custom" },
        { type: "textarea", role: "custom" },
        { type: "select", role: "custom" },
        { type: "button", role: "submit" },
        { type: "button", role: "reset" },
      ],
    },
  ], []);

  const tabOptions: IFormBuilderPanelTabOption[] = useMemo(() => [
    {
      tab: "items",
      label: t("Items"),
    },
    {
      tab: "settings",
      label: t("Settings"),
    },
  ], []);

  function renderSettingsTabContent() {
    return <FormBuilderFormSettings />;
  }

  function renderItemsTabContent() {
    return (
      <div className="form-items">
        {draggableItemCategories.map((category, categoryIndex) => (
          <React.Fragment key={categoryIndex}>
            <div className="form-draggable-category">
              <h3 className="form-draggable-category-heading">{category.heading}</h3>

              <div className="form-draggable-category-items">
                {category.kinds.map((kind, kindIndex) => (
                  <FormBuilderDraggableItem
                    key={kindIndex}
                    kind={kind}
                    metadata={metadataMap.get(getFormBuilderItemKindKey(kind.type, kind.role))}
                  />
                ))}
              </div>
            </div>

            {categoryIndex < draggableItemCategories.length - 1 && <FormBuilderPanelDivider />}
          </React.Fragment>
        ))}
      </div>
    );
  }

  function renderContent() {
    return (
      <div className="form-builder-panel-form-view">
        <div className="form-tabs">
          {tabOptions.map((option, index) => (
            <button
              key={index}
              type="button"
              className={classNames("form-tab", {
                "form-tab-active": activeTab === option.tab,
              })}
              onClick={() => setActiveTab(option.tab)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {activeTab === "settings" ? renderSettingsTabContent() : renderItemsTabContent()}
      </div>
    );
  }

  return (
    <FormBuilderPanelView
      header={{
        heading: t("Form"),
      }}
    >
      {renderContent()}
    </FormBuilderPanelView>
  );
}